import type { DemandeRemboursementDTO } from '@/features/api/extranet/dto/Demande/DR'
import type { ErreurSummaryConfiguration } from '@/features/application/erreur-summary'
import type { SetupDemande } from '@/features/interface/demande/shared'
import { provide, type Component } from 'vue'
import Documents from './steps/documents/DocumentsStep.vue'
import DossierFormation from './steps/dossierformation/DossierFormationStep.vue'
import FinancementStep from './steps/financement/FinancementStep.vue'
import ModuleStep from './steps/modules/ModuleStep.vue'
import SyntheseStep from './steps/synthese/SyntheseStep.vue'
import { DR_ACTIONS, useDRActions } from './useDRActions'
import { useDemandeDRStore } from './useDRStore'

type TabKey =
  | 'Dossier Formation'
  | 'Module 1'
  | 'Module 2'
  | 'Module 3'
  | 'Module 4'
  | 'Module 5'
  | 'Financement'
  | 'Synthèse'
  | 'Documents'

const TABS_VUES: Record<TabKey, Component> = {
  'Dossier Formation': DossierFormation,
  'Module 1': ModuleStep,
  'Module 2': ModuleStep,
  'Module 3': ModuleStep,
  'Module 4': ModuleStep,
  'Module 5': ModuleStep,
  Financement: FinancementStep,
  Synthèse: SyntheseStep,
  Documents,
} as const

const buildConfigFraisModule = (index: number) => {
  const result: ErreurSummaryConfiguration<DemandeRemboursementDTO> = {}
  result[`Module ${index + 1}`] = {
    includeDeep: (state) => [state.frais?.at(index), state.attestations?.at(index)],
    include: (state) => [state.modules?.at(index)],
  }
  return result
}

/* Erwan : JustificatifMission n'existe plus dans natureFichiersDR
function customizeNatureFichiers(store: DemandeStore<'MODEL_DR', LovDrDTO>) {
  const { model, lov } = storeToRefs(store)

  if (!['CDPI', 'CIPI'].includes(model.value!.sousDispositif)) return lov.value.NatureFichier

  return lov.value.NatureFichier.map((item) => {
    if (item.code !== JustificatifMission) return item
    return {
      ...item,
      intitule: 'Justificatif de mission ou de non mission',
    }
  })
}
*/

const TABS_ERR_CONFIG = Object.freeze<ErreurSummaryConfiguration<DemandeRemboursementDTO>>({
  'Dossier Formation': {
    includeDeep: (state) => [state.mission],
    include: (state) => [state.validationNotice],
  },
  ...buildConfigFraisModule(0),
  ...buildConfigFraisModule(1),
  ...buildConfigFraisModule(2),
  ...buildConfigFraisModule(3),
  ...buildConfigFraisModule(4),
  Financement: { includeDeep: (state) => [state.financement], include: (state) => [state.reference] },
  Synthèse: {
    include: (state) => [state.dateSignature, state.lieuSignature, state.acceptationCg, state.attestationSurHonneur],
  },
  Documents: {
    includeDeep: (state) => [state.fichiers],
    include: (state) => [state.attestationPiecesJustificatives],
  },
})

export const SetupDemandeDR: SetupDemande<'MODEL_DR', TabKey> = {
  useStore: useDemandeDRStore,
  injectActions: (store) => {
    const actions = useDRActions(store)
    provide(DR_ACTIONS, actions)
    return actions
  },
  tabFirst: 'Dossier Formation',
  tabsVue: TABS_VUES,
  tabsErrorConfig: (store) => {
    // Dynamise les modules selon le nombre de module de la DR
    const nbModules = store.model?.modules?.length ?? 0
    return Object.entries(TABS_ERR_CONFIG).reduce((tabs, [key, value]) => {
      if (!key.startsWith('Module ')) {
        tabs[key] = value
      } else {
        const numModule = Number(key.split(' ')[1]) - 1
        if (numModule < nbModules) {
          tabs[key] = value
        }
      }
      return tabs
    }, {} as ErreurSummaryConfiguration<DemandeRemboursementDTO>)
  },
  // customizeNatureFichiers,
} as const
